import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormProvider } from '@thinknimble/tn-forms-react'
import {
  CreatePaymentAccountForm,
  TCreatePaymentAccountForm,
  accountTypeOptions,
} from 'src/services/payment-accounts/forms'
import { useTnForm } from '@thinknimble/tn-forms-react'
import { Modal } from '../modal'
import { Input } from '../input'
import { Button } from '../button'
import { ErrorsList, ErrorMessage } from '../errors'
import { paymentAccountApi } from 'src/services/payment-accounts/api'
import { paymentAccountQueries } from 'src/services/payment-accounts/queries'
import { accountTypeLabelMap } from 'src/services/payment-accounts/models'
import { SelectWrapper } from '../select-wrapper'
import { CustomSelect } from '../custom-select'
import { organizationQueries, userQueries } from 'src/services/user'
import { CreatePaymentAccountType, PaymentAccount } from 'src/services/payment-accounts'

interface CreatePaymentAccountModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (paymentAccount: PaymentAccount) => void
  initialName?: string
  /** When provided, the modal edits this account instead of creating a new one. */
  paymentAccount?: PaymentAccount
}

const ownershipOptions = [
  { label: 'Personal', value: 'personal' },
  { label: 'Organization', value: 'organization' },
]

const CreatePaymentAccountModalContent: React.FC<CreatePaymentAccountModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialName = '',
  paymentAccount,
}) => {
  const { t } = useTranslation()
  const { form, createFormFieldChangeHandler } = useTnForm<TCreatePaymentAccountForm>()
  const queryClient = useQueryClient()
  const isEditMode = Boolean(paymentAccount)
  const [accountOwnership, setAccountOwnership] = React.useState<'personal' | 'organization'>(
    paymentAccount?.user ? 'personal' : 'organization',
  )
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: organizations, isLoading: loadingOrgs } = useQuery(organizationQueries.list())
  const { data: users, isLoading: loadingUsers } = useQuery(userQueries.list())

  // Populate the form when the modal opens: prefill from the account in edit
  // mode, otherwise seed the name from initialName.
  useEffect(() => {
    if (!isOpen) return
    if (paymentAccount) {
      createFormFieldChangeHandler(form._name)(paymentAccount.name)
      createFormFieldChangeHandler(form.accountLastFour)(paymentAccount.accountLastFour ?? '')
      createFormFieldChangeHandler(form.institution)(paymentAccount.institution ?? '')
      if (paymentAccount.accountType) {
        createFormFieldChangeHandler(form.accountType)({
          label: accountTypeLabelMap[paymentAccount.accountType],
          value: paymentAccount.accountType,
        })
      }
      if (paymentAccount.organization) {
        setAccountOwnership('organization')
        createFormFieldChangeHandler(form.organization)({
          label: paymentAccount.organizationRef?.name ?? '',
          value: paymentAccount.organization,
        })
      } else if (paymentAccount.user) {
        setAccountOwnership('personal')
        const u = paymentAccount.userRef
        createFormFieldChangeHandler(form.user)({
          label: u ? `${u.firstName} ${u.lastName}` : '',
          value: paymentAccount.user,
        })
      }
    } else if (initialName) {
      createFormFieldChangeHandler(form._name)(initialName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialName, paymentAccount])

  const { mutate: createPaymentAccount, isPending: isCreating } = useMutation({
    mutationFn: async (data: CreatePaymentAccountType) => await paymentAccountApi.create(data),
    onSuccess: (data: PaymentAccount) => {
      queryClient.invalidateQueries({ queryKey: paymentAccountQueries.all() })
      onSuccess(data)
      onClose()
    },
    onError: () => setSubmitError('Could not save the account. Please try again.'),
  })

  const { mutate: updatePaymentAccount, isPending: isUpdating } = useMutation({
    mutationFn: async (data: CreatePaymentAccountType) =>
      await paymentAccountApi.update({ id: paymentAccount!.id, ...data } as any),
    onSuccess: (data: PaymentAccount) => {
      queryClient.invalidateQueries({ queryKey: paymentAccountQueries.all() })
      onSuccess(data)
      onClose()
    },
    onError: () => setSubmitError('Could not save the account. Please try again.'),
  })

  const isPending = isCreating || isUpdating

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    form.validate()
    if (!form.isValid) return
    const payload = form.val as CreatePaymentAccountType
    if (isEditMode) {
      updatePaymentAccount(payload)
    } else {
      createPaymentAccount(payload)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? t('paymentAccountsPage.editTitle') : t('paymentAccountsPage.createTitle')}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-xl border border-border bg-surface p-4">
          <Input
            label={form._name.label}
            placeholder={form._name.placeholder}
            value={form._name.value}
            onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
            onBlur={() => form._name.validate()}
            autoFocus
          />
          <ErrorsList errors={form._name.errors} />

          <SelectWrapper
            label={t('paymentAccountsPage.accountOwnership')}
            onBlur={() => {
              /*noop*/
            }}
          >
            <CustomSelect
              options={ownershipOptions}
              values={ownershipOptions.filter((o) => o.value === accountOwnership)}
              onChange={(values) =>
                setAccountOwnership(values[0].value as 'personal' | 'organization')
              }
            />
          </SelectWrapper>

          {accountOwnership === 'organization' && (
            <>
              <SelectWrapper
                label={form.organization.label}
                onBlur={() => form.organization.validate()}
              >
                <CustomSelect
                  searchable
                  options={
                    organizations?.results?.map((org) => ({ label: org.name, value: org.id })) || []
                  }
                  values={form.organization.value ? [form.organization.value] : []}
                  onChange={(values) => createFormFieldChangeHandler(form.organization)(values[0])}
                  loading={loadingOrgs}
                />
              </SelectWrapper>
              <ErrorsList errors={form.organization.errors} />
            </>
          )}

          {accountOwnership === 'personal' && (
            <>
              <SelectWrapper label={form.user.label} onBlur={() => form.user.validate()}>
                <CustomSelect
                  searchable
                  options={
                    users?.results?.map((user) => ({
                      label: `${user.firstName} ${user.lastName}`,
                      value: user.id,
                    })) || []
                  }
                  values={form.user.value ? [form.user.value] : []}
                  onChange={(values) => createFormFieldChangeHandler(form.user)(values[0])}
                  loading={loadingUsers}
                />
              </SelectWrapper>
              <ErrorsList errors={form.user.errors} />
            </>
          )}
        </div>

        <div className="rounded-xl border border-border bg-surface p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label={form.accountLastFour.label}
                placeholder={form.accountLastFour.placeholder}
                value={form.accountLastFour.value}
                onChange={(e) => createFormFieldChangeHandler(form.accountLastFour)(e.target.value)}
                onBlur={() => form.accountLastFour.validate()}
              />
              <ErrorsList errors={form.accountLastFour.errors} />
            </div>

            <div>
              <Input
                label={form.institution.label}
                placeholder={form.institution.placeholder}
                value={form.institution.value}
                onChange={(e) => createFormFieldChangeHandler(form.institution)(e.target.value)}
                onBlur={() => form.institution.validate()}
              />
              <ErrorsList errors={form.institution.errors} />
            </div>
          </div>

          <SelectWrapper label={form.accountType.label} onBlur={() => form.accountType.validate()}>
            <CustomSelect
              options={accountTypeOptions}
              values={form.accountType.value ? [form.accountType.value] : []}
              onChange={(values) => createFormFieldChangeHandler(form.accountType)(values[0])}
            />
          </SelectWrapper>
          <ErrorsList errors={form.accountType.errors} />
        </div>

        {submitError && (
          <div data-testid="payment-account-submit-error">
            <ErrorMessage>{submitError}</ErrorMessage>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending
              ? isEditMode
                ? t('common.saving')
                : t('common.creating')
              : isEditMode
              ? t('common.save')
              : t('common.create')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export const CreatePaymentAccountModal: React.FC<CreatePaymentAccountModalProps> = (props) => {
  // Reset form when modal opens/closes by using key
  return (
    <FormProvider
      formClass={CreatePaymentAccountForm}
      key={`${props.isOpen ? 'open' : 'closed'}-${props.paymentAccount?.id ?? 'new'}`}
    >
      <CreatePaymentAccountModalContent {...props} />
    </FormProvider>
  )
}
